import React, { memo, useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Broadcast,
  Clipboard,
  Download,
  PauseFill,
  PlayFill,
  Search,
  Send,
  Trash,
  XCircleFill,
  XLg,
} from "react-bootstrap-icons";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/DeviceHealthCheck.css";
import { getDeviceMqttTopics } from "../commonApi";
import { kc } from "../keycloakConfig";

// Admin MQTT test client (like the AWS IoT console's). Talks to device_mgmt_service over a WebSocket
// (/thing/mqtt-test-client, ADMIN only), which holds its own MQTT connection to AWS IoT.
// MqttWorkspace is used both by this page and by the device health MQTT Topics tab.
//
// Keeping the browser responsive:
// - topic suggestions are a small in-page list (no <datalist>: Chrome on Linux draws that as a native
//   window and rebuilds it on every key press, which can freeze the whole desktop);
// - the subscribe/publish fields keep their own state, so typing never re-renders the messages;
// - incoming messages are buffered and added to the list at most 4 times a second;
// - only MAX_MESSAGES are rendered and long payloads are cut until "Show all" is clicked.

const MAX_MESSAGES = 200;
const MAX_STORED = 1000;
const MAX_SUGGESTIONS = 40;
const PAYLOAD_PREVIEW = 6000;
const FLUSH_MS = 250;
const ALL = "__all__";
const DEFAULT_PAYLOAD = '{\n  "message": "Hello from the Mangoe admin portal"\n}';

const GROUP_LABELS = {
  SHORTCUTS: "Shortcuts",
  SHADOW: "Shadow",
  COMMANDS: "Commands",
  OTA: "OTA",
  REQUESTS: "Hub requests",
  PROVISIONING: "Provisioning & logs",
  TERMINAL: "Remote terminal",
};
const GROUP_ORDER = Object.keys(GROUP_LABELS);
const PROBLEM = new Set(["NOT_ALLOWED", "DENIED", "UNKNOWN"]);

const wsUrl = () => {
  const api = process.env.REACT_APP_DeviceAPI_URL || "";
  const base = api.replace(/\/things\/?$/, "").replace(/^http/, "ws");
  return `${base}/thing/mqtt-test-client`;
};

const freshToken = async () => {
  try {
    if (kc?.authenticated) {
      await kc.updateToken(30);
      return kc.token;
    }
  } catch {
    // fall back to the stored token
  }
  return localStorage.getItem("accessToken");
};

const pretty = (payload) => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const filterMatches = (filter, topic) => {
  const f = filter.split("/");
  const t = topic.split("/");
  for (let i = 0; i < f.length; i++) {
    if (f[i] === "#") return true;
    if (i >= t.length || (f[i] !== "+" && f[i] !== t[i])) return false;
  }
  return f.length === t.length;
};

/** A sensible first payload for a topic picked from the list. */
const samplePayload = (topic) => {
  if (/\/shadow\/(get|delete)$/.test(topic)) return "{}";
  if (/\/shadow\/update$/.test(topic)) return '{\n  "state": {\n    "reported": {}\n  }\n}';
  if (/\/campaigns\/get$/.test(topic)) return "{}";
  if (/\/campaigns\/[^/]+\/get$/.test(topic)) return '{\n  "requestType": "DETAILS"\n}';
  if (/\/get_presigned_url$/.test(topic)) return "{}";
  return DEFAULT_PAYLOAD;
};

// Ready-made device tests: optionally publish one message, and listen where the answer arrives.
const deviceTests = (thing, hub) => {
  const tests = [
    {
      key: "shadow-get",
      title: "Get shadow",
      description: "Asks AWS IoT for the current shadow; the answer comes on /get/accepted (or /get/rejected).",
      publish: `$aws/things/${thing}/shadow/get`,
      payload: "{}",
      listen: [`$aws/things/${thing}/shadow/get/+`],
    },
    {
      key: "shadow-watch",
      title: "Watch shadow updates",
      description: "Shows every reported change from the hub as it is accepted or rejected.",
      listen: [`$aws/things/${thing}/shadow/update/+`],
    },
    {
      key: "commands-watch",
      title: "Watch command replies",
      description: "Shows command acknowledgements while you control the device from the app.",
      listen: [`devices/${thing}/commands/response`],
    },
  ];
  if (hub) {
    tests.push(
      {
        key: "ota-list",
        title: "OTA: list campaigns",
        description: "Publishes as the hub to ota/<hub>/campaigns/get; the OTA Lambda answers on .../response.",
        publish: `ota/${hub}/campaigns/get`,
        payload: "{}",
        listen: [`ota/${hub}/campaigns/+/response`],
      },
      {
        key: "ota-details",
        title: "OTA: campaign details",
        description: "Asks for one campaign; replace {campaignId} in the topic.",
        publish: `ota/${hub}/campaigns/{campaignId}/get`,
        payload: '{\n  "requestType": "DETAILS"\n}',
        listen: [`ota/${hub}/campaigns/+/response`],
      },
      {
        key: "ota-url",
        title: "OTA: firmware download URL",
        description: "Asks the presigned-URL Lambda; replace {campaignId} in the topic.",
        publish: `devices/${hub}/campaigns/{campaignId}/get_presigned_url`,
        payload: "{}",
        listen: [`devices/${hub}/campaigns/+/presigned_url`],
      },
      {
        key: "hub-commands",
        title: "Watch hub commands",
        description: "Shows every command the cloud sends to the hub (incl. commands for its child devices).",
        listen: [`devices/${hub}/commands`],
      }
    );
  }
  return tests;
};

/** Text used to search a suggestion: its name plus the topic without the (long, shared) thing names. */
const searchText = (s, ids) => {
  let t = s.topic;
  ids.forEach((id) => {
    if (id) t = t.split(id).join("");
  });
  return `${s.name} ${t}`.toLowerCase();
};

/** Topic for display: long thing names shortened (the full topic is in the tooltip). */
const shortTopic = (topic, ids) =>
  ids.reduce((t, id) => (id && id.length > 24 ? t.split(id).join(`${id.slice(0, 18)}…`) : t), topic);

const groupBy = (items) => {
  const out = [];
  GROUP_ORDER.forEach((g) => {
    const rows = items.filter((i) => i.group === g);
    if (rows.length) out.push([g, rows]);
  });
  return out;
};

/* ------------------------------------------------------------------ topic field with suggestions */

function TopicInput({ value, onChange, suggestions, ids, placeholder, onPick }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const listId = useId();
  const query = useDeferredValue(value);

  const indexed = useMemo(() => suggestions.map((s) => ({ ...s, hay: searchText(s, ids) })), [suggestions, ids]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const found = !q
      ? indexed
      : indexed.filter((s) => s.hay.includes(q) || (q.length >= 8 && s.topic.toLowerCase().includes(q)));
    return found.filter((s) => s.topic !== value).slice(0, MAX_SUGGESTIONS);
  }, [indexed, query, value]);

  const grouped = useMemo(() => groupBy(matches), [matches]);
  const flat = useMemo(() => grouped.flatMap(([, rows]) => rows), [grouped]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector(".is-active");
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (s) => {
    onChange(s.topic);
    onPick?.(s);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open || !flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(flat[active]);
    }
  };

  let row = -1;
  return (
    <div className="mq-combo">
      <input
        className="dhc-input dhc-mono"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-controls={listId}
        aria-expanded={open}
      />
      {open && flat.length > 0 && (
        <div className="mq-suggest" ref={listRef} id={listId} role="listbox">
          {grouped.map(([g, rows]) => (
            <div key={g}>
              <div className="mq-suggest-group">{GROUP_LABELS[g] || g}</div>
              {rows.map((s) => {
                row += 1;
                const i = row;
                return (
                  <div
                    key={s.topic}
                    role="option"
                    aria-selected={i === active}
                    className={`mq-suggest-item ${i === active ? "is-active" : ""}`}
                    // mousedown, not click: the input's blur would close the list before a click lands
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(s);
                    }}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span className="mq-suggest-name">{s.name}</span>
                    <span className={`mq-badge is-${(s.action || "filter").toLowerCase()}`}>{s.action || "Filter"}</span>
                    <span className="mq-suggest-topic dhc-mono" title={s.topic}>
                      {shortTopic(s.topic, ids)}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          <div className="mq-suggest-hint">↑ ↓ to move · Enter to pick · Esc to close</div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ forms (own state: typing stays local) */

const SubscribeForm = memo(function SubscribeForm({ connected, suggestions, ids, placeholder, onSubscribe }) {
  const [filter, setFilter] = useState("");
  const [qos, setQos] = useState(0);
  return (
    <form
      className="dhc-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubscribe(filter, qos);
      }}
    >
      <label className="dhc-label">
        Topic filter
        <span className="dhc-muted dhc-small"> — wildcards + (one level) and # (the rest) are allowed</span>
      </label>
      <TopicInput value={filter} onChange={setFilter} suggestions={suggestions} ids={ids} placeholder={placeholder} />
      <div className="dhc-form-row">
        <label className="dhc-label-inline">
          QoS
          <select className="dhc-input dhc-select" value={qos} onChange={(e) => setQos(Number(e.target.value))}>
            <option value={0}>0 - at most once</option>
            <option value={1}>1 - at least once</option>
          </select>
        </label>
        <button type="submit" className="dhc-btn dhc-btn-primary dhc-btn-sm" disabled={!connected || !filter.trim()}>
          <Broadcast size={13} /> Subscribe
        </button>
      </div>
    </form>
  );
});

const PublishForm = memo(function PublishForm({ connected, suggestions, ids, preset, onPublish }) {
  const [topic, setTopic] = useState(preset?.topic || "");
  const [payload, setPayload] = useState(preset?.payload || DEFAULT_PAYLOAD);
  const [qos, setQos] = useState(0);

  useEffect(() => {
    if (!preset) return;
    setTopic(preset.topic);
    setPayload(preset.payload);
  }, [preset]);

  return (
    <form
      className="dhc-form"
      onSubmit={(e) => {
        e.preventDefault();
        onPublish(topic, payload, qos);
      }}
    >
      <label className="dhc-label">Topic name</label>
      <TopicInput
        value={topic}
        onChange={setTopic}
        suggestions={suggestions}
        ids={ids}
        placeholder="Enter the topic name"
        onPick={(s) => setPayload(samplePayload(s.topic))}
      />
      <label className="dhc-label">Message payload</label>
      <textarea className="dhc-input dhc-mono" rows={6} value={payload} onChange={(e) => setPayload(e.target.value)} spellCheck={false} />
      <div className="dhc-form-row">
        <label className="dhc-label-inline">
          QoS
          <select className="dhc-input dhc-select" value={qos} onChange={(e) => setQos(Number(e.target.value))}>
            <option value={0}>0 - at most once</option>
            <option value={1}>1 - at least once</option>
          </select>
        </label>
        <button type="button" className="dhc-btn dhc-btn-sm" onClick={() => setPayload(pretty(payload))}>
          Format JSON
        </button>
        <button type="submit" className="dhc-btn dhc-btn-primary dhc-btn-sm" disabled={!connected || !topic.trim()}>
          <Send size={12} /> Publish
        </button>
      </div>
    </form>
  );
});

const TestCard = memo(function TestCard({ test, onRun, lastRun, responses }) {
  const [topic, setTopic] = useState(test.publish || "");
  const [payload, setPayload] = useState(test.payload || "");
  const [editing, setEditing] = useState(false);
  return (
    <div className="dhc-test-card">
      <div className="dhc-test-head">
        <div>
          <div className="dhc-test-title">{test.title}</div>
          <div className="dhc-muted dhc-small">{test.description}</div>
        </div>
        <button type="button" className="dhc-btn dhc-btn-primary dhc-btn-sm" onClick={() => onRun(test, topic, payload)}>
          <PlayFill /> {test.publish ? "Send" : "Listen"}
        </button>
      </div>
      {test.publish && (
        <div className="dhc-test-line">
          <span className="dhc-test-label">Publish</span>
          {editing ? (
            <input className="dhc-input dhc-mono" value={topic} onChange={(e) => setTopic(e.target.value)} spellCheck={false} />
          ) : (
            <span className="dhc-mono mq-break">{topic}</span>
          )}
        </div>
      )}
      <div className="dhc-test-line">
        <span className="dhc-test-label">Listen</span>
        <span className="dhc-mono mq-break">{test.listen.join(", ")}</span>
      </div>
      {test.publish && editing && (
        <textarea className="dhc-input dhc-mono" rows={4} value={payload} onChange={(e) => setPayload(e.target.value)} spellCheck={false} />
      )}
      <div className="dhc-test-foot">
        {test.publish && (
          <button type="button" className="dhc-link dhc-small" onClick={() => setEditing(!editing)}>
            {editing ? "Done" : "Edit topic / payload"}
          </button>
        )}
        {lastRun && (
          <span className={`dhc-pill ${responses ? "is-pass" : "is-skipped"}`}>
            {responses ? `${responses} message${responses > 1 ? "s" : ""} received` : "Waiting for messages…"} ·{" "}
            {moment(lastRun).format("HH:mm:ss")}
          </span>
        )}
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ topic list (left column) */

const TopicBrowser = memo(function TopicBrowser({ topics, ids, counts, onSubscribe, onPublish }) {
  const [query, setQuery] = useState("");
  const q = useDeferredValue(query).trim().toLowerCase();
  const rows = useMemo(
    () => (q ? topics.filter((t) => searchText(t, ids).includes(q) || t.topic.toLowerCase().includes(q)) : topics),
    [topics, ids, q]
  );
  const grouped = useMemo(() => groupBy(rows), [rows]);

  return (
    <div className="dhc-panel mq-browser">
      <div className="mq-browser-head">
        <div className="mq-browser-title">
          Device topics <span className="dhc-tab-count">{topics.length}</span>
        </div>
        <div className="dhc-search-field mq-browser-search">
          <Search size={12} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter topics" spellCheck={false} />
        </div>
        <div className="dhc-muted dhc-small">Click a topic to subscribe or publish</div>
      </div>
      <div className="mq-browser-list">
        {grouped.map(([g, list]) => (
          <div key={g}>
            <div className="mq-suggest-group">{GROUP_LABELS[g] || g}</div>
            {list.map((t) => {
              const problem = PROBLEM.has(t.policyStatus);
              const count = counts[t.subscribeFilter] || 0;
              const primary = () => (t.action === "Publish" ? onPublish(t) : onSubscribe(t.subscribeFilter));
              return (
                <div
                  key={t.topic}
                  className={`mq-topic ${problem ? "is-problem" : ""}`}
                  title={
                    problem
                      ? `${t.topic}\n${t.permissions.map((p) => `${p.action}: ${p.reason}`).join("\n")}`
                      : `${t.topic}\n${t.purpose || ""}`
                  }
                  role="button"
                  tabIndex={0}
                  onClick={primary}
                  onKeyDown={(e) => e.key === "Enter" && primary()}
                >
                  <div className="mq-topic-line">
                    {problem && <XCircleFill className="dhc-problem-icon" />}
                    <span className="mq-topic-name">{t.name}</span>
                    {t.scope === "HUB" && <span className="dhc-tag">Hub</span>}
                    <span className={`mq-badge is-${t.action.toLowerCase()}`}>{t.action === "Publish" ? "Pub" : "Sub"}</span>
                    {count > 0 && <span className="mq-count">{count}</span>}
                    <span className="mq-topic-actions">
                      <button
                        type="button"
                        className="dhc-icon-btn"
                        title="Subscribe"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSubscribe(t.subscribeFilter);
                        }}
                      >
                        <Broadcast size={13} />
                      </button>
                      <button
                        type="button"
                        className="dhc-icon-btn"
                        title="Publish"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPublish(t);
                        }}
                      >
                        <Send size={12} />
                      </button>
                    </span>
                  </div>
                  <div className="mq-topic-path dhc-mono">{shortTopic(t.topic, ids)}</div>
                </div>
              );
            })}
          </div>
        ))}
        {!rows.length && <div className="dhc-muted dhc-small dhc-pad">No topics match</div>}
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ messages */

const MessageRow = memo(function MessageRow({ m, onCopy }) {
  const [full, setFull] = useState(false);
  const long = m.text.length > PAYLOAD_PREVIEW;
  return (
    <div className="dhc-msg">
      <div className="dhc-msg-meta">
        <span className="dhc-mono dhc-msg-topic mq-break">{m.topic}</span>
        <span className="dhc-muted dhc-small dhc-nowrap">
          {moment(m.receivedAt).format("HH:mm:ss.SSS")} · QoS {m.qos}
        </span>
        <button type="button" className="dhc-icon-btn" title="Copy payload" onClick={() => onCopy(m.payload)}>
          <Clipboard size={12} />
        </button>
      </div>
      <pre className="dhc-raw dhc-msg-payload">{long && !full ? `${m.text.slice(0, PAYLOAD_PREVIEW)}\n…` : m.text}</pre>
      {long && (
        <button type="button" className="dhc-link dhc-small" onClick={() => setFull(!full)}>
          {full ? "Show less" : `Show all (${Math.round(m.text.length / 1024)} KB)`}
        </button>
      )}
    </div>
  );
});

const MessagesPanel = memo(function MessagesPanel({ subs, messages, selected, ids, onSelect, onPause, onUnsubscribe, onClear, onCopy }) {
  const counts = useMemo(() => {
    const c = {};
    messages.forEach((m) => {
      c[m.subscription] = (c[m.subscription] || 0) + 1;
    });
    return c;
  }, [messages]);
  const shown = useMemo(
    () => (selected === ALL ? messages : messages.filter((m) => m.subscription === selected)).slice(0, MAX_MESSAGES),
    [messages, selected]
  );

  const exportMessages = () => {
    const rows = shown.map(({ subscription, topic, qos, payload, receivedAt }) => ({ subscription, topic, qos, payload, receivedAt }));
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mqtt-messages-${moment().format("YYYYMMDD-HHmmss")}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="dhc-panel mq-messages">
      <div className="mq-chips">
        <button type="button" className={`mq-chip ${selected === ALL ? "is-active" : ""}`} onClick={() => onSelect(ALL)}>
          All messages <span className="mq-count">{messages.length}</span>
        </button>
        {subs.map((s) => (
          <div key={s.filter} className={`mq-chip ${selected === s.filter ? "is-active" : ""} ${s.paused ? "is-paused" : ""}`}>
            <button type="button" className="mq-chip-main" onClick={() => onSelect(s.filter)} title={s.filter}>
              <span className={`dhc-conn-dot ${s.active ? "is-connected" : "is-connecting"}`} />
              <span className="mq-chip-name dhc-mono">{shortTopic(s.filter, ids)}</span>
              <span className="mq-count">{counts[s.filter] || 0}</span>
            </button>
            <button type="button" className="dhc-icon-btn" title={s.paused ? "Resume" : "Pause"} onClick={() => onPause(s.filter)}>
              {s.paused ? <PlayFill size={12} /> : <PauseFill size={12} />}
            </button>
            <button type="button" className="dhc-icon-btn" title="Unsubscribe" onClick={() => onUnsubscribe(s.filter)}>
              <XLg size={10} />
            </button>
          </div>
        ))}
        {!subs.length && <span className="dhc-muted dhc-small">No subscriptions yet</span>}
      </div>
      <div className="dhc-msg-head">
        <span className="dhc-mono dhc-msg-title mq-break">{selected === ALL ? "All messages" : selected}</span>
        <div className="dhc-msg-actions">
          <button type="button" className="dhc-btn dhc-btn-sm" onClick={() => onClear(selected)} disabled={!shown.length}>
            <Trash size={12} /> Clear
          </button>
          <button type="button" className="dhc-btn dhc-btn-sm" onClick={exportMessages} disabled={!shown.length}>
            <Download size={12} /> Export
          </button>
        </div>
      </div>
      <div className="dhc-msg-list">
        {shown.map((m) => (
          <MessageRow key={m.id} m={m} onCopy={onCopy} />
        ))}
        {!shown.length && (
          <div className="dhc-empty">
            {subs.length ? "No messages yet. Messages appear here as soon as they arrive." : "Subscribe to a topic to see its messages here."}
          </div>
        )}
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ workspace */

export function MqttWorkspace({ thing, topicsData, initialSub = [], initialPub = "" }) {
  const [conn, setConn] = useState({ state: "CONNECTING" });
  const [subs, setSubs] = useState([]); // [{filter, qos, active, paused}]
  const [messages, setMessages] = useState([]); // newest first
  const [selected, setSelected] = useState(ALL);
  const [tab, setTab] = useState(initialPub ? "publish" : thing ? "tests" : "subscribe");
  const [preset, setPreset] = useState(initialPub ? { topic: initialPub, payload: samplePayload(initialPub) } : null);
  const [runs, setRuns] = useState({}); // test key -> {at, listen}

  const wsRef = useRef(null);
  const pending = useRef({});
  const seq = useRef(0);
  const subsRef = useRef(subs);
  subsRef.current = subs;
  const wantedRef = useRef(initialSub);
  const inbox = useRef([]);

  // add buffered messages to the list a few times a second instead of once per message
  useEffect(() => {
    const timer = setInterval(() => {
      if (!inbox.current.length) return;
      const batch = inbox.current.reverse();
      inbox.current = [];
      setMessages((prev) => [...batch, ...prev].slice(0, MAX_STORED));
    }, FLUSH_MS);
    return () => clearInterval(timer);
  }, []);

  const send = useCallback(
    (body) =>
      new Promise((resolve, reject) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          reject(new Error("Not connected"));
          return;
        }
        const id = String(++seq.current);
        pending.current[id] = { resolve, reject };
        ws.send(JSON.stringify({ ...body, id }));
      }),
    []
  );

  const subscribe = useCallback(
    async (filter, qos = 0, { quiet = false } = {}) => {
      const f = (filter || "").trim();
      if (!f) return;
      if (subsRef.current.some((s) => s.filter === f && s.active)) {
        setSelected(f);
        return;
      }
      setSubs((prev) => [...prev.filter((s) => s.filter !== f), { filter: f, qos, active: false, paused: false }]);
      try {
        await send({ type: "subscribe", topic: f, qos });
        setSubs((prev) => prev.map((s) => (s.filter === f ? { ...s, active: true } : s)));
        setSelected(f);
        if (!quiet) toast.success(`Subscribed to ${f}`, { toastId: `sub-${f}` });
      } catch (e) {
        setSubs((prev) => prev.filter((s) => s.filter !== f));
        toast.error(`Subscribe failed: ${e.message}`, { toastId: `sub-err-${f}` });
      }
    },
    [send]
  );

  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
    setConn({ state: "CONNECTING" });
    let ws;
    try {
      const token = await freshToken();
      ws = new WebSocket(`${wsUrl()}${token ? `?access_token=${encodeURIComponent(token)}` : ""}`);
    } catch (e) {
      setConn({ state: "ERROR", message: `Could not open the WebSocket: ${e.message}` });
      return;
    }
    wsRef.current = ws;

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.type === "status") {
        setConn(msg);
        if (msg.state === "CONNECTED") {
          // restore earlier subscriptions after a reconnect, then the ones requested in the URL
          const again = subsRef.current.map((s) => s.filter);
          setSubs([]);
          [...new Set([...again, ...wantedRef.current])].forEach((f) => subscribe(f, 0, { quiet: true }));
          wantedRef.current = [];
        }
      } else if (msg.type === "ack" || msg.type === "error") {
        const p = pending.current[msg.id];
        if (p) {
          delete pending.current[msg.id];
          if (msg.type === "ack") p.resolve(msg);
          else p.reject(new Error(msg.message));
        } else if (msg.type === "error") {
          toast.error(msg.message, { toastId: "mqtt-error" });
        }
      } else if (msg.type === "message") {
        if (subsRef.current.find((s) => s.filter === msg.subscription)?.paused) return;
        if (inbox.current.length >= MAX_STORED) inbox.current.shift();
        inbox.current.push({ ...msg, id: `${msg.receivedAt}-${++seq.current}`, text: pretty(msg.payload) });
      } else if (msg.type === "dropped") {
        toast.warn(`${msg.count} messages dropped (more than 50 per second)`, { toastId: "mqtt-dropped" });
      }
    };
    ws.onclose = (event) => {
      if (wsRef.current !== ws) return;
      Object.values(pending.current).forEach((p) => p.reject(new Error("Connection closed")));
      pending.current = {};
      setSubs((prev) => prev.map((s) => ({ ...s, active: false })));
      setConn((c) =>
        c.state === "DISABLED"
          ? c
          : {
              ...c,
              state: event.code === 1008 ? "FORBIDDEN" : "DISCONNECTED",
              message: event.code === 1008 ? "You need the ADMIN role to use the MQTT test client" : c.message,
            }
      );
    };
    ws.onerror = () => setConn((c) => ({ ...c, state: "ERROR", message: "WebSocket connection failed" }));
  }, [subscribe]);

  const disconnect = () => {
    const ws = wsRef.current;
    if (ws) ws.close();
  };

  useEffect(() => {
    connect();
    return () => {
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) ws.close();
    };
  }, [connect]);

  const unsubscribe = useCallback(
    async (filter) => {
      try {
        await send({ type: "unsubscribe", topic: filter });
      } catch {
        // connection gone: drop it locally anyway
      }
      setSubs((prev) => prev.filter((s) => s.filter !== filter));
      setMessages((prev) => prev.filter((m) => m.subscription !== filter));
      setSelected((cur) => (cur === filter ? ALL : cur));
    },
    [send]
  );

  const publish = useCallback(
    async (topic, payload, qos = 0) => {
      const t = (topic || "").trim();
      if (/[{}]/.test(t)) {
        toast.error("Replace the {placeholder} in the topic first", { toastId: "mqtt-placeholder" });
        return false;
      }
      try {
        await send({ type: "publish", topic: t, payload, qos });
        toast.success(`Published to ${t}`, { toastId: `pub-${t}-${Date.now()}` });
        return true;
      } catch (e) {
        toast.error(`Publish failed: ${e.message}`, { toastId: "mqtt-pub-error" });
        return false;
      }
    },
    [send]
  );

  const runTest = useCallback(
    async (test, topic, payload) => {
      for (const f of test.listen) {
        await subscribe(f, 0, { quiet: true });
      }
      if (test.publish && !(await publish(topic, payload))) return;
      setRuns((prev) => ({ ...prev, [test.key]: { at: Date.now(), listen: test.listen } }));
      setSelected(test.listen.length === 1 ? test.listen[0] : ALL);
    },
    [subscribe, publish]
  );

  const togglePause = useCallback(
    (filter) => setSubs((prev) => prev.map((s) => (s.filter === filter ? { ...s, paused: !s.paused } : s))),
    []
  );

  const clear = useCallback(
    (sel) => setMessages((prev) => (sel === ALL ? [] : prev.filter((m) => m.subscription !== sel))),
    []
  );

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied", { toastId: "mqtt-copied" });
    } catch {
      toast.error("Could not copy to clipboard");
    }
  }, []);

  const pickSubscribe = useCallback((filter) => subscribe(filter), [subscribe]);
  const pickPublish = useCallback((t) => {
    setPreset({ topic: t.topic, payload: samplePayload(t.topic) });
    setTab("publish");
  }, []);

  const hub = topicsData?.hubThingName || thing || null;
  const deviceTopics = useMemo(() => topicsData?.topics || [], [topicsData]);
  const ids = useMemo(() => [thing, hub].filter(Boolean), [thing, hub]);
  const tests = useMemo(() => (thing ? deviceTests(thing, hub) : []), [thing, hub]);

  const subSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (s) => {
      if (!s.topic || seen.has(s.topic)) return;
      seen.add(s.topic);
      out.push(s);
    };
    if (thing) add({ group: "SHORTCUTS", name: "All shadow topics", topic: `$aws/things/${thing}/shadow/#` });
    if (hub) {
      add({ group: "SHORTCUTS", name: "All hub device topics", topic: `devices/${hub}/#` });
      add({ group: "SHORTCUTS", name: "All hub OTA topics", topic: `ota/${hub}/#` });
    }
    deviceTopics.forEach((t) => add({ group: t.group, name: t.name, action: t.action, topic: t.subscribeFilter }));
    return out;
  }, [deviceTopics, thing, hub]);

  const pubSuggestions = useMemo(
    () => deviceTopics.filter((t) => t.action === "Publish").map((t) => ({ group: t.group, name: t.name, action: t.action, topic: t.topic })),
    [deviceTopics]
  );

  // messages per device topic, for the counters in the topic list
  const topicCounts = useMemo(() => {
    const c = {};
    if (!deviceTopics.length || !messages.length) return c;
    const filters = [...new Set(deviceTopics.map((t) => t.subscribeFilter))];
    messages.forEach((m) => {
      filters.forEach((f) => {
        if (filterMatches(f, m.topic)) c[f] = (c[f] || 0) + 1;
      });
    });
    return c;
  }, [deviceTopics, messages]);

  const responses = useMemo(() => {
    const out = {};
    Object.entries(runs).forEach(([key, run]) => {
      out[key] = messages.filter((m) => m.receivedAt >= run.at && run.listen.some((f) => filterMatches(f, m.topic))).length;
    });
    return out;
  }, [runs, messages]);

  const connected = conn.state === "CONNECTED";

  return (
    <div className="mq-workspace">
      <div className="dhc-panel dhc-conn">
        <span className={`dhc-conn-dot is-${conn.state.toLowerCase()}`} />
        <div className="dhc-conn-main">
          <b>
            {connected ? "Connected" : conn.state === "CONNECTING" ? "Connecting…" : conn.state === "DISABLED" ? "Disabled" : "Disconnected"}
          </b>
          <span className="dhc-muted dhc-small">
            {conn.message || (conn.clientId ? `Client ${conn.clientId} · ${conn.endpoint}` : "")}
          </span>
        </div>
        {connected ? (
          <button type="button" className="dhc-btn dhc-btn-sm" onClick={disconnect}>
            Disconnect
          </button>
        ) : (
          <button type="button" className="dhc-btn dhc-btn-sm" onClick={connect} disabled={conn.state === "CONNECTING" || conn.state === "DISABLED"}>
            Connect
          </button>
        )}
      </div>

      <div className={`mq-grid ${deviceTopics.length ? "" : "is-single"}`}>
        {deviceTopics.length > 0 && (
          <TopicBrowser topics={deviceTopics} ids={ids} counts={topicCounts} onSubscribe={pickSubscribe} onPublish={pickPublish} />
        )}

        <div className="mq-main">
          <div className="dhc-panel">
            <div className="dhc-tabs">
              {[
                ...(thing ? [["tests", "Device tests"]] : []),
                ["subscribe", "Subscribe to a topic"],
                ["publish", "Publish to a topic"],
              ].map(([key, label]) => (
                <button key={key} type="button" className={`dhc-tab ${tab === key ? "is-active" : ""}`} onClick={() => setTab(key)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="dhc-tab-body">
              {tab === "tests" && (
                <div className="dhc-test-grid">
                  {tests.map((t) => (
                    <TestCard key={t.key} test={t} onRun={runTest} lastRun={runs[t.key]?.at} responses={responses[t.key] || 0} />
                  ))}
                </div>
              )}
              {tab === "subscribe" && (
                <SubscribeForm
                  connected={connected}
                  suggestions={subSuggestions}
                  ids={ids}
                  placeholder={thing ? `$aws/things/${thing}/shadow/#` : "Enter the topic filter"}
                  onSubscribe={subscribe}
                />
              )}
              {tab === "publish" && (
                <PublishForm connected={connected} suggestions={pubSuggestions} ids={ids} preset={preset} onPublish={publish} />
              )}
            </div>
          </div>

          <MessagesPanel
            subs={subs}
            messages={messages}
            selected={selected}
            ids={ids}
            onSelect={setSelected}
            onPause={togglePause}
            onUnsubscribe={unsubscribe}
            onClear={clear}
            onCopy={copy}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ full-page version (/devices/mqtt-test-client) */

function MqttTestClient() {
  const [params] = useSearchParams();
  const thing = params.get("thing") || "";
  const [topicsData, setTopicsData] = useState(null);
  const [initialSub] = useState(() => params.getAll("sub"));
  const [initialPub] = useState(() => params.get("pub") || "");

  useEffect(() => {
    if (!thing) return;
    getDeviceMqttTopics(thing)
      .then((res) => setTopicsData(res.data?.data || null))
      .catch(() => setTopicsData(null));
  }, [thing]);

  return (
    <div className="dhc">
      <div className="dhc-page-head">
        <div>
          {thing && (
            <Link className="dhc-btn dhc-btn-sm mq-back" to={`/devices/health/${encodeURIComponent(thing)}?tab=mqtt`}>
              <ArrowLeft size={13} /> Back to device health
            </Link>
          )}
          <h2 className="dhc-page-title">MQTT test client</h2>
          <p className="dhc-muted dhc-small">
            Subscribe to and publish MQTT messages through AWS IoT to check that a device's topics work. Messages you
            publish reach real devices and Lambdas in this environment.
          </p>
        </div>
        {thing && (
          <span className="dhc-pill is-info dhc-mono mq-break" title="Device">
            {thing}
          </span>
        )}
      </div>
      <MqttWorkspace thing={thing} topicsData={topicsData} initialSub={initialSub} initialPub={initialPub} />
    </div>
  );
}

export default MqttTestClient;
